import type { FC } from "react";

import { useHistory } from "react-router";

import { Text, toastError, toastSuccess } from "@alpacahq/ui/old";

import type { UseGetAllRelationshipsReturn } from "~/v2/api/hooks/useGetAllRelationships";
import { Path } from "~/v2/path";
import type {
  AchRelationshipWithBankInfo,
  Relationship,
} from "../common/types";
import { isACHPendingMicroDeposit } from "../common/util";
import { LinkedAccountCard } from "./LinkedAccountCard";
import { useUnlinkRelationship } from "./useUnlinkRelationship";

export const LinkedAccounts: FC<{
  accountID: string;
  relationships: UseGetAllRelationshipsReturn;
}> = ({ accountID, relationships: { ach, wire, currencyCloud } }) => {
  const history = useHistory();

  const {
    unlinkRelationship,
    isLoading: isUnlinkLoading,
    unlinkModal,
  } = useUnlinkRelationship({
    accountID,
    onSuccess: (relationship) => {
      if (relationship?.type === "ACH") {
        ach.refetch();
      } else if (relationship?.type === "Wire") {
        wire.refetch();
      }
      toastSuccess(`${relationship.type} connection removed`);
    },
    onError: () => {
      toastError(`An error occurred while removing the bank connection.`);
    },
  });

  const onVerifyACHClicked =
    (relationship: AchRelationshipWithBankInfo) => () => {
      history.push({
        pathname: Path.ROUTE_FUNDING_LINK_ACH,
        search: `?micro_deposit_verification_required=true&relationship_id=${relationship.id}`,
      });
    };

  const onUnlinkRelationshipClicked = (relationship: Relationship) => () =>
    unlinkRelationship(relationship);

  const hasNoRelationships =
    ach.relationships.length === 0 &&
    wire.relationships.length === 0 &&
    !currencyCloud.account;

  if (hasNoRelationships) {
    return null;
  }

  return (
    <section>
      <Text className="pb-4 font-semibold text-xl">Linked Accounts</Text>
      <div className="flex w-full max-w-lg flex-col gap-4 pb-4">
        {ach.relationships.map((relationship) => {
          const isMicroDepositVerificationRequired =
            isACHPendingMicroDeposit(relationship);

          return (
            <LinkedAccountCard
              key={`ach-${relationship.id}`}
              relationship={{
                ...relationship,
                type: "ACH",
              }}
              onUnlinkClicked={
                isUnlinkLoading
                  ? undefined
                  : onUnlinkRelationshipClicked({
                      ...relationship,
                      type: "ACH",
                    })
              }
              onVerifyClicked={
                isMicroDepositVerificationRequired
                  ? onVerifyACHClicked(relationship)
                  : undefined
              }
            />
          );
        })}
        {wire.relationships.map((relationship) => (
          <LinkedAccountCard
            key={`wire-${relationship.id}`}
            relationship={{ ...relationship, type: "Wire" }}
            onUnlinkClicked={
              isUnlinkLoading
                ? undefined
                : onUnlinkRelationshipClicked({
                    ...relationship,
                    type: "Wire",
                  })
            }
          />
        ))}
        {currencyCloud.account && (
          <LinkedAccountCard
            relationship={{
              ...currencyCloud.account,
              type: "CurrencyCloud",
            }}
          />
        )}
      </div>

      {unlinkModal}
    </section>
  );
};
